import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

interface Props {
  value: string;
  onChange: (url: string) => void;
  folder?: string;
  label?: string;
}

export function ImageUpload({ value, onChange, folder = "uploads", label = "Image" }: Props) {
  const [busy, setBusy] = useState(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from("site-images").upload(path, file, { upsert: false });
    if (error) { toast.error(error.message); setBusy(false); return; }
    const { data } = supabase.storage.from("site-images").getPublicUrl(path);
    onChange(data.publicUrl);
    setBusy(false);
    toast.success("Image uploaded");
  }

  return (
    <div className="space-y-2">
      {label && <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>}
      {value && (
        <div className="relative inline-block">
          <img src={value} alt="" className="h-24 w-24 rounded-lg object-cover ring-1 ring-border" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 rounded-full bg-destructive p-1 text-destructive-foreground shadow"
            aria-label="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <label className="inline-flex">
        <Button asChild size="sm" variant="outline" disabled={busy}>
          <span className="cursor-pointer">
            <Upload className="mr-1.5 h-4 w-4" />
            {busy ? "Uploading..." : value ? "Change photo" : "Choose photo from device"}
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={busy} />
          </span>
        </Button>
      </label>
      <p className="text-xs text-muted-foreground">Pick an image from your phone or computer — no URL needed.</p>
    </div>
  );
}
